/**
 * ActionRequiredBanner — Batch #14.52
 *
 * Bannière rouge proéminente qui affiche les actions requises pour une section
 * de paramètres donnée. Consomme l'API /api/settings/health et filtre les issues
 * pour la `sectionKey` passée en prop (cabinet, invoice, smtp, notifications, etc.).
 *
 * - Mobile + desktop responsive
 * - N'affiche RIEN si tout est OK pour cette section
 * - Distingue les issues "(recommandé)" (ambre/warn) des bloquantes (rouge/error)
 * - Optionnel : bouton quick-fix custom
 *
 * Usage :
 *   <ActionRequiredBanner sectionKey="cabinet" title="Action requise" />
 *
 * Avec un quick-fix :
 *   <ActionRequiredBanner
 *     sectionKey="invoice"
 *     quickFix={{ label: "Utiliser FAC- par défaut", onClick: () => update('prefix','FAC-') }}
 *   />
 */
import { useEffect, useState } from "react";
import { AlertCircle, Info } from "lucide-react";
import { api } from "@/api";

export default function ActionRequiredBanner({
  sectionKey,
  title = "Action requise",
  quickFix = null,
  testId,
}) {
  const [issues, setIssues] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/settings/health")
      .then((r) => {
        if (!active) return;
        const data = r.data || {};
        const section = data[sectionKey];
        // Pour cabinet : on agrège aussi les issues SMTP car la tuile Cabinet remonte SMTP
        let allIssues = section?.issues || [];
        if (sectionKey === "cabinet" && data.smtp?.issues) {
          allIssues = [...allIssues, ...data.smtp.issues];
        }
        // Sépare les "(recommande)" des bloquants
        const blockers = allIssues.filter((i) => !/recommand/i.test(i));
        const recos = allIssues.filter((i) => /recommand/i.test(i));
        setIssues(blockers);
        setRecommended(recos);
        setLoaded(true);
      })
      .catch(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [sectionKey]);

  if (!loaded) return null;
  if (issues.length === 0 && recommended.length === 0) return null;

  const hasBlockers = issues.length > 0;
  const palette = hasBlockers
    ? {
        bg: "bg-rose-50",
        border: "border-rose-300",
        iconBg: "bg-rose-100 border-rose-300",
        icon: "text-rose-700",
        title: "text-rose-900",
        body: "text-rose-800",
        btn: "bg-rose-600 hover:bg-rose-700 text-white",
        Icon: AlertCircle,
      }
    : {
        bg: "bg-amber-50",
        border: "border-amber-300",
        iconBg: "bg-amber-100 border-amber-300",
        icon: "text-amber-700",
        title: "text-amber-900",
        body: "text-amber-800",
        btn: "bg-amber-600 hover:bg-amber-700 text-white",
        Icon: Info,
      };

  const Icon = palette.Icon;
  const finalTitle = hasBlockers ? title : "Suggestion d'amélioration";

  return (
    <div
      className={`${palette.bg} border-2 ${palette.border} rounded-xl p-4 flex items-start gap-3 sm:gap-4`}
      data-testid={testId || `action-required-${sectionKey}`}
      role="alert"
    >
      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full ${palette.iconBg} border-2 flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${palette.icon}`} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className={`text-sm sm:text-base font-bold ${palette.title} mb-1.5`}>
          {finalTitle}
        </h4>
        <ul className={`text-xs sm:text-sm ${palette.body} leading-relaxed space-y-0.5 list-disc list-inside`}>
          {issues.map((iss) => <li key={`b-${iss}`}><strong>{iss}</strong></li>)}
          {recommended.map((iss) => <li key={`r-${iss}`} className="opacity-80">{iss}</li>)}
        </ul>
        {quickFix && hasBlockers && (
          <button
            type="button"
            onClick={quickFix.onClick}
            className={`mt-3 text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1 ${palette.btn}`}
            data-testid={`action-required-${sectionKey}-quickfix`}
          >
            {quickFix.label}
          </button>
        )}
      </div>
    </div>
  );
}
